const CLOSED_STAGE_CONSTANT = "closed";
const WIN_SUBSTAGE_CONSTANTS = new Set(["win", "won"]);
const JOB_STAGE_CONSTANTS = new Set(["construction in progress", "completed"]);

// Mirrors the @HiddenDefaults set in fn_FilterLeads.sql: whole stages hidden
// from the lead list unless explicitly requested via stageFilters, plus the
// Win sub-stage of Closed (note: backend checks "Win" only, not "Won").
const DEFAULT_HIDDEN_STAGE_CONSTANTS = new Set([
  "completed",
  "contract preparation",
  "construction in progress",
  "lost",
]);
const DEFAULT_HIDDEN_CLOSED_SUBSTAGE_CONSTANT = "win";

const normalize = (value) => String(value || "").toLowerCase().trim();

export const isClosedStage = (stageConstant) =>
  normalize(stageConstant) === CLOSED_STAGE_CONSTANT;

export const isWinSubStage = (subStageConstant) =>
  WIN_SUBSTAGE_CONSTANTS.has(normalize(subStageConstant));

/**
 * Is this stage one of the 3 canonical "job stages"
 * (Closed+Win, Construction In Progress, Completed)?
 * `subStageConstant` is optional: omit it to check stage identity alone
 * (e.g. filtering the master stage list); pass it to also require a
 * Win/Won sub-stage when the stage is Closed (e.g. deciding whether a
 * lead has actually reached a job stage).
 */
export const isJobStage = (stageConstant, subStageConstant) => {
  if (isClosedStage(stageConstant)) {
    return subStageConstant === undefined || isWinSubStage(subStageConstant);
  }
  return JOB_STAGE_CONSTANTS.has(normalize(stageConstant));
};

/**
 * Is this stage hidden from the lead list by default (unless explicitly
 * requested via stageFilters)? True for Lost, Completed, Contract Preparation,
 * and Construction In Progress — matches fn_FilterLeads.sql's @HiddenDefaults.
 */
export const isDefaultHiddenStage = (stageConstant) =>
  DEFAULT_HIDDEN_STAGE_CONSTANTS.has(normalize(stageConstant));

/**
 * Flat leaf ids (stage ids for whole stages, sub-stage ids for Closed) that
 * should appear pre-selected in the Stage/Sub Stage filter when the user
 * hasn't made an explicit choice — i.e. everything except the stages
 * fn_FilterLeads.sql hides by default when @StageFilters is NULL.
 */
export const getDefaultVisibleStageIds = (stages = []) => {
  const ids = [];
  stages.forEach((stage) => {
    if (isDefaultHiddenStage(stage.stageConstant)) return;
    if (isClosedStage(stage.stageConstant)) {
      const subStages = stage.subStages || stage.SubStages || [];
      subStages.forEach((sub) => {
        if (normalize(sub.subStageConstant) !== DEFAULT_HIDDEN_CLOSED_SUBSTAGE_CONSTANT) {
          ids.push(sub.subStageId);
        }
      });
    } else {
      ids.push(stage.stageId);
    }
  });
  return ids;
};

/**
 * Explicit stageFilters payload representing every stage, including the ones
 * hidden by default — used by the Stage/Sub Stage filter's "All" option.
 */
export const getAllStagesFilterPayload = (stages = []) =>
  stages.map((stage) => ({ stageId: stage.stageId, subStageIds: null }));

export const isLeadConvertedToJob = (leadObj) =>
  Boolean(
    leadObj?.IsConvertedToJob ??
    leadObj?.isConvertedToJob ??
    leadObj?.IsConverted ??
    leadObj?.isConverted
  );

/**
 * Should changing a lead to `stageData` warn the user that it will cancel
 * the lead's associated Job? True only when the lead is already converted
 * AND the target stage/sub-stage isn't one of the 3 job stages.
 */
export const shouldWarnJobCancellation = (stageData, leadObj) => {
  if (!stageData || !isLeadConvertedToJob(leadObj)) return false;
  const stageConstant = stageData?.stageConstant || stageData?.StageConstant || "";
  const subStageConstant = stageData?.subStageConstant || stageData?.SubStageConstant || "";
  return !isJobStage(stageConstant, subStageConstant);
};

/**
 * Should moving a lead to stageData prompt the user to reactivate an associated cancelled job?
 * True if lead is converted (has a non-deleted job) AND target stage/sub-stage is a canonical job stage.
 */
export const shouldOfferJobReactivation = (stageData, leadObj) => {
  if (!stageData || !isLeadConvertedToJob(leadObj)) return false;
  const stageConstant = stageData?.stageConstant || stageData?.StageConstant || "";
  const subStageConstant = stageData?.subStageConstant || stageData?.SubStageConstant || "";
  return isJobStage(stageConstant, subStageConstant);
};
